/**
 * C6 — Subservice organizations (#99, reworked from "carve-outs").
 *
 * Each subservice org the report declares renders as a CUEC-style card
 * (SubserviceCard): editable summary/title/treatment, a per-card Comments
 * thread, and the decision flow (Request SOC 1 / document N/A). The AI only
 * summarizes — the user decides. Decisions key on the stable carve-out id (#88)
 * so editing can't orphan them.
 *
 * Macro (section) level: an editable section intro + a 3-button row
 * (Edit intro · Add subservice org · Add comment → section Comments thread).
 */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
  type SelectProps,
} from "@cloudscape-design/components";
import type { Report, ScopeCheck } from "@/core/api/types";
import { CommentThread } from "@/core/ui/CommentThread";
import { addCarveout, deleteCarveout, editCarveout, listReports } from "./api";
import { REQUEST_SENTINEL, SubserviceCard, type SubserviceRow } from "./SubserviceCard";

interface CarveoutRow {
  id?: string;
  name?: string;
  treatment?: string;
  applicability_rationale?: string;
  page?: number;
  manual?: boolean;
}

type CarveoutOverride = { name?: string; treatment?: string; summary?: string };

const TREATMENT_OPTS: SelectProps.Option[] = [
  { label: "Carved out (excluded — we need a separate SOC 1)", value: "carved_out" },
  { label: "Inclusive (covered by this report)", value: "inclusive" },
];

export function CarveoutsList({
  check,
  reportId,
  onUpdated,
  openTargets,
  onOpenReport,
}: {
  check: ScopeCheck;
  reportId: string;
  onUpdated: () => void;
  /** #119: target_ids of the report's OPEN scope action items — a carve-out card
   *  is Open iff its key is in here (matches the section count exactly). */
  openTargets: Set<string>;
  /** #C6: navigate to a carve-out's linked/placeholder SOC 1 report. */
  onOpenReport?: (reportId: string) => void;
}) {
  const ov = (check.user_overrides ?? {}) as {
    carveouts_decisions?: Record<string, boolean>;
    carveouts_notes?: Record<string, string>;
    carveouts_coverage?: Record<string, string>;
    carveouts_list?: CarveoutRow[];
    carveouts_overrides?: Record<string, CarveoutOverride>;
  };
  const decisions = ov.carveouts_decisions ?? {};
  const coverage = ov.carveouts_coverage ?? {};
  const overrides = ov.carveouts_overrides ?? {};
  const keyOf = (c: CarveoutRow): string => c.id ?? c.name ?? "(unnamed)";
  const toRow = (c: CarveoutRow, manual: boolean): SubserviceRow => {
    const o = overrides[keyOf(c)] ?? {};
    return {
      key: keyOf(c),
      name: o.name ?? c.name ?? "(unnamed)",
      treatment: o.treatment ?? c.treatment,
      summary: o.summary ?? c.applicability_rationale,
      page: c.page,
      manual,
    };
  };
  const aiCarveouts = (check.ai_data?.carveouts as CarveoutRow[] | undefined) ?? [];
  const rows: SubserviceRow[] = [
    ...aiCarveouts.map((c) => toRow(c, false)),
    ...(ov.carveouts_list ?? []).map((c) => toRow(c, true)),
  ];

  const [reports, setReports] = useState<Report[]>([]);
  // #C6: bump after a decision so listReports re-runs — a "Request SOC 1" may have
  // just created a placeholder report whose name + status the cards need to show.
  const [reportsNonce, setReportsNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addTreatment, setAddTreatment] = useState("carved_out");
  const [addSummary, setAddSummary] = useState("");
  const [editTarget, setEditTarget] = useState<{
    carveout_id: string;
    name: string;
    treatment: string;
    summary: string;
  } | null>(null);
  // Macro section: section-level comments toggle. C6 has no narrative/intro
  // text anymore — when orgs exist we show only the cards; when there are zero
  // we show a single deterministic "none" line (below). The old
  // subservice_explanation is gone, so there's no "Edit intro" control here.
  const [showSectionComments, setShowSectionComments] = useState(false);

  useEffect(() => {
    void listReports().then((r) => setReports(r.filter((x) => x.id !== reportId)));
  }, [reportId, reportsNonce]);

  // After a carve-out decision a placeholder SOC 1 report may have been created —
  // refresh the parent AND the local reports list so the new report + its status
  // surface in the card's "Subservice SOC 1" FYI line.
  const refreshAll = () => {
    onUpdated();
    setReportsNonce((n) => n + 1);
  };

  const submitAdd = async () => {
    setBusy(true);
    try {
      await addCarveout(check.id, {
        name: addName.trim(),
        treatment: addTreatment,
        summary: addSummary.trim() || null,
      });
      setAddOpen(false);
      setAddName("");
      setAddTreatment("carved_out");
      setAddSummary("");
      onUpdated();
    } finally {
      setBusy(false);
    }
  };

  const removeCarveout = async (key: string) => {
    setBusy(true);
    try {
      await deleteCarveout(check.id, key);
      onUpdated();
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    setBusy(true);
    try {
      await editCarveout(check.id, {
        carveout_id: editTarget.carveout_id,
        name: editTarget.name.trim(),
        treatment: editTarget.treatment,
        summary: editTarget.summary.trim() || null,
      });
      setEditTarget(null);
      onUpdated();
    } finally {
      setBusy(false);
    }
  };

  const reportOptions: SelectProps.Option[] = [
    { label: "Request it — open an action item to obtain the SOC 1", value: REQUEST_SENTINEL },
    ...reports.map((r) => ({
      label: r.service_org_name || "(untitled report)",
      description: r.scope_title ?? undefined,
      value: r.id,
    })),
  ];

  const openEdit = (row: SubserviceRow) =>
    setEditTarget({
      carveout_id: row.key,
      name: row.name,
      treatment: row.treatment ?? "carved_out",
      summary: row.summary ?? "",
    });

  // Macro row: add a subservice org + a section-level Comments thread.
  const macro = (
    <SpaceBetween size="s">
      <SpaceBetween direction="horizontal" size="xs">
        <Button iconName="add-plus" onClick={() => setAddOpen(true)}>
          Add subservice organization
        </Button>
        <Button iconName="contact" onClick={() => setShowSectionComments((v) => !v)}>
          Add comment
        </Button>
      </SpaceBetween>
      {showSectionComments && <CommentThread kind="scope-check" targetId={check.id} onChange={onUpdated} />}
    </SpaceBetween>
  );

  const modals = (
    <>
      <Modal
        visible={addOpen}
        onDismiss={() => setAddOpen(false)}
        header="Add subservice organization"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setAddOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void submitAdd()}
                disabled={busy || !addName.trim() || !addSummary.trim()}
                loading={busy}
              >
                Add
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box>
            <Box variant="awsui-key-label">Subservice organization name</Box>
            <Input value={addName} onChange={({ detail }) => setAddName(detail.value)} placeholder="e.g. AWS" />
          </Box>
          <Box>
            <Box variant="awsui-key-label">Treatment</Box>
            <Select
              selectedOption={TREATMENT_OPTS.find((o) => o.value === addTreatment) ?? TREATMENT_OPTS[0]!}
              onChange={({ detail }) => setAddTreatment(detail.selectedOption.value ?? "carved_out")}
              options={TREATMENT_OPTS}
            />
          </Box>
          <Box>
            <Box variant="awsui-key-label">Explanation</Box>
            <Textarea
              value={addSummary}
              onChange={({ detail }) => setAddSummary(detail.value)}
              rows={3}
              placeholder="What this subservice organization is and what it does for the service organization"
            />
          </Box>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={!!editTarget}
        onDismiss={() => setEditTarget(null)}
        header="Edit subservice organization"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setEditTarget(null)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void submitEdit()} disabled={busy || !editTarget?.name.trim()} loading={busy}>
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {editTarget && (
          <SpaceBetween size="s">
            <Box>
              <Box variant="awsui-key-label">Subservice organization name</Box>
              <Input value={editTarget.name} onChange={({ detail }) => setEditTarget({ ...editTarget, name: detail.value })} />
            </Box>
            <Box>
              <Box variant="awsui-key-label">Treatment</Box>
              <Select
                selectedOption={TREATMENT_OPTS.find((o) => o.value === editTarget.treatment) ?? TREATMENT_OPTS[0]!}
                onChange={({ detail }) => setEditTarget({ ...editTarget, treatment: detail.selectedOption.value ?? "carved_out" })}
                options={TREATMENT_OPTS}
              />
            </Box>
            <Box>
              <Box variant="awsui-key-label">Summary (what this subservice does)</Box>
              <Textarea
                value={editTarget.summary}
                onChange={({ detail }) => setEditTarget({ ...editTarget, summary: detail.value })}
                rows={3}
                placeholder="Correct or add the description"
              />
            </Box>
          </SpaceBetween>
        )}
      </Modal>
    </>
  );

  if (!rows.length)
    return (
      <SpaceBetween size="s">
        <Box color="text-body-secondary">No subservice organization in the report.</Box>
        {macro}
        {modals}
      </SpaceBetween>
    );

  return (
    <SpaceBetween size="s">
      {rows.map((row) => (
        <SubserviceCard
          key={row.key}
          co={row}
          scopeCheckId={check.id}
          reportId={reportId}
          open={openTargets.has(row.key)}
          decided={decisions[row.key]}
          coveredBy={coverage[row.key]}
          coverName={reports.find((r) => r.id === coverage[row.key])?.service_org_name}
          coverStatus={reports.find((r) => r.id === coverage[row.key])?.status}
          reportOptions={reportOptions}
          onEdit={() => openEdit(row)}
          onDelete={() => void removeCarveout(row.key)}
          onUpdated={refreshAll}
          onOpenReport={onOpenReport}
        />
      ))}
      {macro}
      {modals}
    </SpaceBetween>
  );
}
